import { v4 as uuid } from 'uuid';

// One request in flight per card; different cards save independently.
export function createCrmMoveQueue({ send, onChange, onSaved, delay = 200 }) {
    const entries = new Map();
    let sequence = 0;
    const emit = () => onChange(Object.fromEntries([...entries].map(([id, entry]) => [id, {
        status: entry.status, message: entry.error?.message, conflict: entry.error?.conflict,
    }])));
    const run = async (id) => {
        const entry = entries.get(id);
        if (!entry || entry.inFlight) return;
        clearTimeout(entry.timer);
        // A newly arrived neighbor may still be saving its own stage change.
        // Wait for earlier dependencies, while unrelated cards remain independent.
        const dependencies = [entry.latest.before_id, entry.latest.after_id]
            .map(neighbor => entries.get(String(neighbor)))
            .filter(neighbor => neighbor && neighbor.order < entry.order && neighbor.status !== 'error'
                && String(neighbor.latest.id_etapa) === String(entry.latest.id_etapa));
        if (dependencies.length) {
            entry.timer = setTimeout(() => run(id), delay);
            return;
        }
        const intent = entry.latest;
        entry.inFlight = true;
        entry.status = 'saving';
        emit();
        try {
            const data = await send(id, { ...intent, expected_version: entry.version });
            entry.version = data.crm_version;
            const latest = intent.operation_id === entry.latest.operation_id;
            onSaved(id, data, latest);
            if (latest) entries.delete(id);
            else {
                entry.status = 'pending';
                entry.timer = setTimeout(() => run(id), delay);
            }
        } catch (error) {
            // Keep the desired position visible and make the failure explicit; never undo other cards.
            entry.status = 'error';
            entry.error = error;
        } finally {
            entry.inFlight = false;
            emit();
        }
    };
    return {
        enqueue(id, payload, version) {
            id = String(id);
            let entry = entries.get(id);
            if (!entry) {
                entry = { version: Number(version) || 0, inFlight: false, order: ++sequence };
                entries.set(id, entry);
            }
            clearTimeout(entry.timer);
            entry.latest = { ...payload, operation_id: uuid() };
            if (!entry.inFlight) {
                entry.status = 'pending';
                entry.error = null;
                entry.timer = setTimeout(() => run(id), delay);
            }
            emit();
        },
        retry(id, version) {
            id = String(id);
            const entry = entries.get(id);
            if (!entry || entry.inFlight) return;
            if (version != null) {
                entry.version = Number(version);
                entry.latest.operation_id = uuid();
            }
            entry.error = null;
            run(id);
        },
        hasPending: () => entries.size > 0,
    };
}
